import React from 'react';
import { cn } from './cn';
import { Icon } from './base/Icon';

/**
 * EmptyState (เดสก์ท็อป) — แยกสองกรณีให้ชัด เพราะทางออกไม่เหมือนกัน
 *   'no-data'      ยังไม่มีข้อมูลในระบบเลย → ทางออกคือสร้าง/นำเข้า
 *   'no-results'   ตัวกรองไม่เจอ → ทางออกคือล้างตัวกรอง
 *   'queue-clear'  คิวว่าง = ข่าวดี ไม่ใช่ข้อผิดพลาด (ใช้โทนบวก)
 *   'no-access'    ไม่มีสิทธิ์เข้าถึง (RBAC ตาม FR-ADM-36) พร้อมบอกว่าติดต่อใคร
 */
export type EmptyKind = 'no-data' | 'no-results' | 'queue-clear' | 'no-access';

const PRESET: Record<EmptyKind, { icon: string; tone: string }> = {
  'no-data': { icon: 'inbox', tone: 'text-[var(--ink-600)]' },
  'no-results': { icon: 'search_off', tone: 'text-[var(--ink-600)]' },
  'queue-clear': { icon: 'task_alt', tone: 'text-[var(--success-text)]' },
  'no-access': { icon: 'lock', tone: 'text-[var(--warning-text)]' },
};

export interface EmptyStateProps {
  kind: EmptyKind;
  title: string;
  description?: string;
  /** ปุ่มทางออก — no-results ควรเป็น 'ล้างตัวกรอง' */
  action?: { label: string; onClick: () => void };
  className?: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({ kind, title, description, action, className }) => {
  const p = PRESET[kind];
  return (
    <div
      role={kind === 'no-access' ? 'alert' : undefined}
      className={cn('flex flex-col items-center gap-[var(--space-2)] py-[var(--space-8)] text-center font-[family-name:var(--font-thai)]', className)}
    >
      <Icon name={p.icon} size={40} filled className={p.tone} />
      <p className="text-[length:var(--text-section)] font-[var(--weight-semibold)] leading-[var(--leading-heading)] text-[var(--ink-900)]">{title}</p>
      {description && (
        <p className="max-w-[46ch] text-[length:var(--text-form-lg)] leading-[var(--leading-thai)] text-[var(--ink-700)]">{description}</p>
      )}
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="mt-[var(--space-1)] min-h-[var(--hit-primary)] rounded-[var(--radius-sm)] border border-[var(--line-300)] bg-[var(--surface-0)] px-[var(--space-4)] text-[length:var(--text-form)] font-[var(--weight-medium)] text-[var(--blue-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
        >
          {action.label}
        </button>
      )}
    </div>
  );
};
